import {
  Background,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryScatter,
  VictoryContainer,
} from "victory"
import { colors } from "../../../utils/theme"

/* Default values to control styling */
const DEFAULT_CHART_PADDING = { top: 16, right: 16, bottom: 15, left: 19 }
const DEFAULT_DOT_SIZE = 5
const DEFAULT_GROUP_OFFSET = 0.15
const MIN_Y_DOMAIN_MAX = 3

/* Axis tick styling */
const axisTickLabelStyle = {
  fill: colors.safetyPlan.graphText,
  fontWeight: 400,
  lineHeight: 16,
  fontSize: 12,
  padding: 0,
  textAlign: "center" as const,
}

/* Callers use this interface to specify descriptors to tell the chart which 
 numeric fields on each row should be rendered as separate dot stacks */
export interface StackedDotPlotSeries<T extends object> {
  key: string
  label: string
  valueKey: keyof T
  color: string
  strokeColor?: string
}

type DotDatum = {
  x: number
  y: number
}

/* Normalizes to non-negative integer */
function normalizeCount(value: unknown) {
  return typeof value === "number" && value > 0 ? Math.floor(value) : 0
}

/* Spacing logic for series sharing the same x bucket */
function getSeriesOffset(
  seriesIndex: number,
  seriesCount: number,
  groupOffset: number
) {
  if (seriesCount === 1) {
    return 0
  }

  const midpoint = (seriesCount - 1) / 2
  return (seriesIndex - midpoint) * groupOffset
}

interface StackedDotPlotProps<T extends object> {
  data: T[]
  xKey: keyof T
  series: StackedDotPlotSeries<T>[]
  xTickFormatter?: (value: string) => string
  yTickFormatter?: (value: number) => string
  yTicks?: number[]
  maxY?: number
  height?: number
  width?: number
  dotSize?: number
  groupOffset?: number
  /**
   * When true, Victory scales the SVG to the parent width using `width`/`height`
   * as the aspect ratio. Prefer this inside fluid layouts to avoid overflow.
   */
  responsive?: boolean
}

/**
 * Renders a grouped stacked-dot plot from row-based data.
 *
 * Each item in `data` represents one x-axis bucket.
 * `xKey` selects the label field for that bucket.
 * Each entry in `series` points to a numeric field on the row via `valueKey`.
 * That numeric value is interpreted as a count and rendered as a vertical stack
 * of dots. When multiple series are provided, their stacks are offset
 * horizontally within the same x-axis bucket so they appear side-by-side.
 *
 * @prop {T[]} data - x-axis bucket data
 * @prop {keyof T} xKey - selects the label field for that bucket
 * @prop {StackedDotPlotSeries} series - Descriptors that point to a numeric field on
 * the row via 'valueKey'. Contains metadata about group coloring.
 * @prop {(value: string) => string} xTickFormatter - Optional formatter for x-axis tick labels.
 * @prop {(value: number) => string} yTickFormatter - Optional formatter for y-axis tick labels.
 * @prop {number[]} yTicks - Optionally specify y tick values.
 * @prop {number} maxY - Optional maximum y-axis value.
 * @prop {number} height - Optionally specify bar chart height
 * @prop {number} width - Optionally specify bar chart width
 * @prop {boolean} responsive - Scale chart to parent width (aspect from width/height)
 */
export default function StackedDotPlot<T extends object>({
  data,
  xKey,
  series,
  xTickFormatter,
  yTickFormatter,
  yTicks,
  maxY,
  height,
  width,
  dotSize = DEFAULT_DOT_SIZE,
  groupOffset = DEFAULT_GROUP_OFFSET,
  responsive = false,
}: StackedDotPlotProps<T>) {
  const xDomain: [number, number] | undefined =
    data.length > 0 ? [0.5, data.length + 0.5] : undefined
  const tickValues = data.map((_, index) => index + 1)

  const scatterSeries = series.map((seriesConfig, seriesIndex) => {
    const dots = data.flatMap((datum, dataIndex) => {
      const count = normalizeCount(datum[seriesConfig.valueKey])
      const xValue =
        dataIndex +
        (1 + getSeriesOffset(seriesIndex, series.length, groupOffset))

      return Array.from({ length: count }, (_, dotIndex) => ({
        x: xValue,
        y: dotIndex + 1,
      }))
    })

    return {
      ...seriesConfig,
      dots,
    }
  })

  const computedMaxY =
    maxY ??
    Math.max(
      ...scatterSeries.flatMap((item) => item.dots.map((dot) => dot.y)),
      ...(yTicks ?? []),
      1
    )
  const yDomainMax = Math.max(computedMaxY, MIN_Y_DOMAIN_MAX)

  return (
    <VictoryChart
      domain={{
        x: xDomain,
        y: [0, yDomainMax],
      }}
      containerComponent={<VictoryContainer responsive={responsive} />}
      domainPadding={{ x: 20, y: 8 }}
      padding={DEFAULT_CHART_PADDING}
      width={width}
      height={height}
      title="Frequency"
      backgroundComponent={
        <Background
          style={{
            fill: colors.neutral.white,
          }}
        />
      }
    >
      <VictoryAxis
        dependentAxis
        tickValues={yTicks}
        tickFormat={yTickFormatter}
        tickLabelComponent={<VictoryLabel dx={8} />}
        style={{
          axis: {
            stroke: "transparent",
          },
          tickLabels: axisTickLabelStyle,
          grid: {
            stroke: "transparent",
          },
        }}
      />
      <VictoryAxis
        tickValues={tickValues}
        tickLabelComponent={<VictoryLabel />}
        tickFormat={(tick: number) => {
          const rawValue = data[tick - 1]?.[xKey]
          if (rawValue === undefined || rawValue === null) {
            return ""
          }

          const label = String(rawValue)
          return xTickFormatter ? xTickFormatter(label) : label
        }}
        style={{
          axis: {
            stroke: "transparent",
          },
          tickLabels: axisTickLabelStyle,
          grid: {
            stroke: "transparent",
          },
        }}
      />
      {scatterSeries.map((seriesConfig) => (
        <VictoryScatter
          key={seriesConfig.key}
          data={seriesConfig.dots as DotDatum[]}
          size={dotSize}
          style={{
            data: {
              fill: seriesConfig.color,
              stroke: seriesConfig.strokeColor ?? seriesConfig.color,
            },
          }}
        />
      ))}
    </VictoryChart>
  )
}
